import React from 'react';
import '../styles/theme.css';
import '../styles/LandingPage.css';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';

// Image paths
const IMAGES = {
  hero: '/hero.png',

  
  // Feature card images
  demographic: '/demographic_Analysis.png',
  market: '/Market_Potential.png',
  competitive: '/CompetitiveLandscape.png',
  
  // Industry card images
  entertainment: '/FamilyEntertainmentCenter.png',
  retail: '/RetailBusiness.png',
  daycare: '/Daycare.png',
  restaurant: '/RestaurantsCafes.png',
};

const LandingPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleLoginClick = () => {
    if (isAuthenticated) {
      navigate('/welcome');
    } else {
      navigate('/login');
    }
  };

  return (
    <div style={{ backgroundColor: '#f0f0f0', minHeight: '100vh' }}>
      {/* Hero Section */}
      {/* TODO: Add hero image back to this section later */}
      <section 
        className="hero-section" 
        style={{
          backgroundColor: '#1e1e1e'
        }}
      >
        <h1 className="hero-section__title">
          Your <span className="hero-section__highlight">AI Co-Founder</span> for Franchise Discovery and Location-Based Business Growth
        </h1>
        <p className="hero-section__subtitle">
          AI-Powered Tools for Site Selection, Performance Monitoring, and Growth
        </p>
        <p className="hero-section__description animated-text">
          <span className="animated-word">Discover.</span>{' '}
          <span className="animated-word">Launch.</span>{' '}
          <span className="animated-word">Optimize.</span>{' '}
          <span className="animated-word">Grow.</span>{' '}
          — All in One Intelligent Platform.
        </p>
        <Button onClick={handleLoginClick}>
          Explore Opportunities
        </Button>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <h2 className="section-title">Comprehensive Location Intelligence</h2>
        <div className="card-grid">
          <FeatureCard 
            title="Demographic Analysis" 
            description="Deep insights into population density, income levels, age distribution, and consumer behavior patterns"
            image={IMAGES.demographic}
          />
          <FeatureCard 
            title="Market Potential" 
            description="Evaluate regional demand and economic viability to identify high-opportunity markets for your business"
            image={IMAGES.market}
          />
          <FeatureCard 
            title="Competitive Landscape" 
            description="Detailed mapping of competitors, complementary businesses, and market saturation"
            image={IMAGES.competitive}
          />
        </div>
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <Button onClick={handleLoginClick}>
            Run a Sample Analysis
          </Button>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="light-section">
        <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
          <h2 className="section-title">Smarter Than a Consultant. Faster Than a Franchise Broker.</h2>
          <div style={{ maxWidth: '800px', margin: '0 auto 2rem' }}>
            <p style={{ fontSize: '1.1rem', marginBottom: '2rem' }}>
              Our AI-powered platform delivers location insights in minutes, not weeks - giving you the confidence to make strategic business decisions.
            </p>
          </div>
          <h3 className="section-title" style={{ fontSize: '1.6rem', marginBottom: '2rem' }}>How It Works</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '2rem', marginBottom: '3rem' }}>
            <div style={{ flex: '1 1 300px', maxWidth: '350px', textAlign: 'center' }}>
              <div style={{ 
                backgroundColor: '#7FE7D9', 
                color: '#1e1e1e', 
                width: '60px', 
                height: '60px', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                margin: '0 auto 1rem',
                fontSize: '1.5rem',
                fontWeight: 'bold'
              }}>1</div>
              <h4 style={{ fontSize: '1.3rem', marginBottom: '0.5rem' }}>Enter your business type & location</h4>
              <p>Tell us about your business and where you're considering opening a location</p>
            </div>
            <div style={{ flex: '1 1 300px', maxWidth: '350px', textAlign: 'center' }}>
              <div style={{ 
                backgroundColor: '#7FE7D9', 
                color: '#1e1e1e', 
                width: '60px', 
                height: '60px', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                margin: '0 auto 1rem',
                fontSize: '1.5rem',
                fontWeight: 'bold'
              }}>2</div>
              <h4 style={{ fontSize: '1.3rem', marginBottom: '0.5rem' }}>Get AI insights instantly</h4>
              <p>Our platform analyzes thousands of data points to generate location-specific recommendations</p>
            </div>
            <div style={{ flex: '1 1 300px', maxWidth: '350px', textAlign: 'center' }}>
              <div style={{ 
                backgroundColor: '#7FE7D9', 
                color: '#1e1e1e', 
                width: '60px', 
                height: '60px', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                margin: '0 auto 1rem',
                fontSize: '1.5rem',
                fontWeight: 'bold'
              }}>3</div>
              <h4 style={{ fontSize: '1.3rem', marginBottom: '0.5rem' }}>Make smarter decisions backed by data</h4>
              <p>Use our comprehensive reports to select optimal locations with confidence</p>
            </div>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <Button onClick={handleLoginClick}>
              Run a Sample Analysis
            </Button>
          </div>
          <div style={{ 
            borderTop: '1px solid #ddd', 
            paddingTop: '2rem', 
            marginTop: '2rem',
            color: '#777',
            fontSize: '0.9rem'
          }}>
            <p>Trusted by forward-thinking entrepreneurs and location planners across the country</p>
            <div style={{ 
              marginTop: '1rem', 
              display: 'flex', 
              justifyContent: 'center', 
              gap: '2rem',
              opacity: 0.7
            }}>
              {/* Placeholder for client logos */}
              <div>Client logos coming soon</div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="dark-section">
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 className="section-title section-title--light">Why Choose Our Platform?</h2>
          <div className="card-grid">
            <FeatureCard 
              title="AI-Powered Insights" 
              description="Leverage machine learning algorithms trained on vast datasets to identify optimal locations"
              dark
            />
            <FeatureCard 
              title="Industry-Specific Analysis" 
              description="Tailored insights for retail, entertainment centers, daycare facilities, and more"
              dark
            />
            <FeatureCard 
              title="Risk Mitigation" 
              description="Identify potential challenges and opportunities before investing in a location"
              dark
            />
          </div>
        </div>
      </section>

      {/* Advanced Features Section */}
      <section className="features-section">
        <h2 className="section-title">Advanced Capabilities</h2>
        <div className="card-grid">
          <FeatureCard 
            title="Revenue Forecasting" 
            description="Site-specific financial projections to predict performance metrics for your exact location and business model"
          />
          <FeatureCard 
            title="Foot Traffic Analysis" 
            description="Understand pedestrian and vehicle movement patterns to optimize visibility and accessibility"
          />
          <FeatureCard 
            title="Safety & Crime Analysis" 
            description="Comprehensive safety metrics to ensure secure environments for businesses and customers"
          />
        </div>
      </section>

      {/* Target Industries Section */}
      <section className="light-section">
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 className="section-title">Industries We Serve</h2>
          <div className="card-grid card-grid--industries">
            <IndustryCard 
              title="Family Entertainment Centers" 
              description="Optimize locations for maximum foot traffic and family accessibility"
              image={IMAGES.entertainment}
            />
            <IndustryCard 
              title="Retail Businesses" 
              description="Find prime retail locations with ideal customer demographics"
              image={IMAGES.retail}
            />
            <IndustryCard 
              title="Daycare Facilities" 
              description="Identify safe locations with high concentrations of young families"
              image={IMAGES.daycare}
            />
            <IndustryCard 
              title="Restaurants & Cafes" 
              description="Discover high-potential locations with the right customer base"
              image={IMAGES.restaurant}
            />
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="cta-section">
        <h2 className="cta-section__title">Ready to Find Your Ideal Location?</h2>
        <p className="cta-section__description">
          Join businesses that are making data-driven location decisions to maximize their success potential.
        </p>
        <Button onClick={handleLoginClick}>
          Find My Location
        </Button>
      </section>
    </div>
  );
};

// Feature Card Component
const FeatureCard: React.FC<{ 
  title: string; 
  description: string; 
  dark?: boolean; 
  image?: string 
}> = ({ title, description, dark, image }) => (
  <div className={`feature-card ${dark ? 'feature-card--dark' : ''}`}>
    {image && (
      <div className="feature-card__image-container">
        <img 
          src={image} 
          alt={title} 
          className="feature-card__image"
        />
      </div>
    )}
    <h3 className="feature-card__title">{title}</h3>
    <p className="feature-card__description">{description}</p>
  </div>
);

// Industry Card Component
const IndustryCard: React.FC<{ 
  title: string; 
  description: string; 
  image: string 
}> = ({ title, description, image }) => (
  <div className="industry-card">
    <div className="industry-card__image-container">
      <img 
        src={image} 
        alt={title} 
        className="industry-card__image"
      />
    </div>
    <h3 className="industry-card__title">{title}</h3>
    <p className="industry-card__description">{description}</p>
  </div>
);

export default LandingPage;
